import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export type RFQCompilePhase = 'idle' | 'generating' | 'compiling' | 'done' | 'error';

export interface RFQCompileProgress {
  phase: RFQCompilePhase;
  done: number;
  total: number;
  error?: string;
}

interface RFQCompileContextValue {
  progress: RFQCompileProgress;
  start: (total: number) => void;
  markGenerated: (count: number) => void;
  beginCompile: () => void;
  finish: () => void;
  fail: (error: string) => void;
  reset: () => void;
}

const initialProgress: RFQCompileProgress = { phase: 'idle', done: 0, total: 0 };

const RFQCompileContext = createContext<RFQCompileContextValue | null>(null);

export const RFQCompileProvider = ({ children }: { children: ReactNode }) => {
  const [progress, setProgress] = useState<RFQCompileProgress>(initialProgress);

  const start = useCallback((total: number) => {
    setProgress({ phase: 'generating', done: 0, total });
  }, []);

  const markGenerated = useCallback((count: number) => {
    setProgress((prev) => ({ ...prev, done: count }));
  }, []);

  const beginCompile = useCallback(() => {
    setProgress((prev) => ({ ...prev, phase: 'compiling' }));
  }, []);

  const finish = useCallback(() => {
    setProgress((prev) => ({ ...prev, phase: 'done' }));
  }, []);

  const fail = useCallback((error: string) => {
    setProgress((prev) => ({ ...prev, phase: 'error', error }));
  }, []);

  const reset = useCallback(() => {
    setProgress(initialProgress);
  }, []);

  const value = useMemo<RFQCompileContextValue>(
    () => ({ progress, start, markGenerated, beginCompile, finish, fail, reset }),
    [progress, start, markGenerated, beginCompile, finish, fail, reset],
  );

  return <RFQCompileContext.Provider value={value}>{children}</RFQCompileContext.Provider>;
};

export const useRFQCompile = (): RFQCompileContextValue => {
  const ctx = useContext(RFQCompileContext);
  if (!ctx) throw new Error('useRFQCompile must be used within RFQCompileProvider');
  return ctx;
};

export const isRFQCompileActive = (progress: RFQCompileProgress): boolean =>
  progress.phase === 'generating' || progress.phase === 'compiling';
